import clsx from 'clsx'
import { AnimatePresence } from 'framer-motion'
import React, { MouseEvent, ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'
import useBreakpoint from '../hooks/ui/useBreakpoint'
import Fade from './Animations/Fade'
import Slide from './Animations/Slide'
import IconButton from './IconButton'
import Icon from './Icon/Icon'

export interface ModalProps {
  open?: boolean
  children?: ReactNode
  className?: string
  onClose?: () => void
  hideCloseButton?: boolean
  fullscreen?: boolean
}

const CloseButton = ({ onClose }: Pick<ModalProps, 'onClose'>) => {
  return (
    <IconButton className='absolute right-1 top-1' onClick={onClose}>
      <Icon name='close' />
    </IconButton>
  )
}

const Modal: React.FC<ModalProps> = ({
  open,
  children,
  className,
  onClose,
  hideCloseButton = false,
  fullscreen,
}: ModalProps) => {
  const isSm = useBreakpoint('sm')

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
  }, [open])

  const modalClasses = clsx(
    'relative transform overflow-hidden bg-paper shadow-xl p-4',
    fullscreen && 'grow flex flex-col justify-center',
    'sm:my-8 sm:w-full sm:max-w-lg sm:rounded-md', // Desktop classes
    'rounded-t-md w-full max-w-full' // Mobile classes
  )

  const handleClose = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    onClose?.()
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className='relative z-40' role='dialog' aria-modal='true'>
          <Fade scale={1} className='overlay' aria-hidden='true' />

          <div className='fixed inset-0 z-50 w-screen overflow-y-auto'>
            <div
              onClick={handleClose}
              className={clsx(
                'flex min-h-full',
                fullscreen && 'flex-col',
                'p-0 items-end justify-stretch xs:justify-center sm:items-center sm:p-4' // Mobile classes
              )}
            >
              {isSm ? (
                <Slide
                  onClick={e => e.stopPropagation()}
                  className={clsx(modalClasses, className)}
                  position={'bottom'}
                >
                  {!hideCloseButton && <CloseButton onClose={onClose} />}
                  {children}
                </Slide>
              ) : (
                <Fade
                  onClick={e => e.stopPropagation()}
                  scale={0.9}
                  className={clsx(modalClasses, className)}
                >
                  {!hideCloseButton && <CloseButton onClose={onClose} />}
                  {children}
                </Fade>
              )}
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body.querySelector('#root') || document.body
  )
}

export default Modal
